<template>
  <div class="kb" role="group" :aria-label="STRINGS.aria.labels.onscreenKeyboard">
    <div class="kb-rows">
      <div class="kb-row">
        <button
          v-for="char in rows[0]"
          :key="char"
          type="button"
          class="kb-key kb-key--letter"
          :disabled="disabled"
          :aria-label="char.toUpperCase()"
          @click="onKey(char)"
        >
          {{ char.toUpperCase() }}
        </button>
      </div>

      <div class="kb-row kb-row--pad">
        <button
          v-for="char in rows[1]"
          :key="char"
          type="button"
          class="kb-key kb-key--letter"
          :disabled="disabled"
          :aria-label="char.toUpperCase()"
          @click="onKey(char)"
        >
          {{ char.toUpperCase() }}
        </button>
      </div>

      <div class="kb-row">
        <span class="kb-spacer" aria-hidden="true" />
        <div class="kb-mid">
          <button
            v-for="char in rows[2]"
            :key="char"
            type="button"
            class="kb-key kb-key--letter"
            :disabled="disabled"
            :aria-label="char.toUpperCase()"
            @click="onKey(char)"
          >
            {{ char.toUpperCase() }}
          </button>
        </div>
        <button
          type="button"
          class="kb-key kb-key--sp kb-key--del"
          :disabled="disabled"
          :aria-label="STRINGS.aria.labels.deleteLetter"
          @click="onDelete"
        >
          <svg width="24" height="17" viewBox="0 0 23 17" aria-hidden="true">
            <path
              d="M7 1h13a2 2 0 012 2v11a2 2 0 01-2 2H7l-6-7.5L7 1z"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linejoin="round"
            />
            <path
              d="M10 5l7 7M17 5l-7 7"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import STRINGS from '@/constants/strings';

const props = withDefaults(defineProps<{ disabled?: boolean }>(), { disabled: false });

const emit = defineEmits<{
  key: [char: string];
  delete: [];
}>();

// Uppercase-only QWERTY. No space bar (game words contain no spaces) and no
// 123/symbol key — that's the whole point of a custom in-app keyboard. Submit
// lives in the input dock above, so the keyboard carries letters + delete only
// (no redundant Enter key).
const rows = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
] as const;

const onKey = (char: string) => {
  if (props.disabled) return;
  emit('key', char);
};
const onDelete = () => {
  if (props.disabled) return;
  emit('delete');
};
</script>

<style scoped>
.kb {
  height: 100%;
  min-height: 210px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: #0b0b0e;
  /* Clear the iOS home-indicator band so the bottom row sits inside the curved
     screen; env() resolves to the real inset on notch devices, 8px elsewhere. */
  padding-bottom: max(env(safe-area-inset-bottom, 0px), 8px);
  /* Clear left/right insets too under viewport-fit=cover (0 in portrait — keys are
     flex and shrink, so this only insets the keyboard in landscape). */
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
}

.kb-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 5px;
}

.kb-row {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
}

.kb-row--pad {
  padding: 0 18px;
}

.kb-mid {
  display: flex;
  gap: 6px;
  flex: 1;
}

.kb-spacer {
  flex: 0 0 auto;
  width: 46px;
}

.kb-key {
  height: 44px;
  min-width: 0;
  border: none;
  border-radius: 6px;
  background: #2a2722;
  color: var(--color-arena-text-bright);
  font-family: var(--font-mono);
  font-size: 19px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 1px 0 rgba(0, 0, 0, 0.5);
  transition:
    background 0.08s,
    transform 0.08s;
}

.kb-key--letter {
  flex: 1;
}

.kb-key:active:not(:disabled) {
  background: var(--color-energy-primary);
  color: #1a1612;
  transform: translateY(1px);
}

.kb-key--sp {
  background: #1c1a16;
  flex: 0 0 auto;
  width: 46px;
}

.kb-key:disabled {
  opacity: 0.4;
  cursor: default;
}

@media (prefers-reduced-motion: reduce) {
  .kb-key {
    transition: none;
  }
  .kb-key:active:not(:disabled) {
    transform: none;
  }
}
</style>
